/** Formas de dado do painel (specs 004 e 007) e ajudas pra chamar a API. Regra de negocio fica no backend. */
import { ErroApi, ehInstante, formatarDataHora, formatarReais, mensagemDe, type EstadoDoComprovante, type EstadoNaCorretora, type ResumoDoSaldo, type Tom } from "@mtc/ui";
import { useCallback, useState } from "react";
import { useSessao, type Operador } from "./sessao";

export type Ambiente = "local" | "homologacao" | "producao";
export type Capacidades = { marca: string; modulos: string[]; ambiente?: Ambiente };

export type Etapa = "TESTE" | "SIMULADOR" | "MESA_REAL";
export type Familia = "EXAME" | "FAST" | "BLACK";
export type Plataforma = "PROFIT_ONE" | "PROFIT_PRO";
export type StatusMatricula = "AGUARDANDO_INICIO" | "ATIVA" | "SUSPENSA" | "ENCERRADA";
export type StatusSolicitacao = "PENDENTE" | "AGUARDANDO_PAGAMENTO" | "APROVADA" | "RECUSADA" | "CANCELADA" | "REVOGADA";
export type Pessoinha = { id: string; nome: string; cpf: string };
export type Auditoria = { em: string; ator: string; acao: string; antes: unknown; depois: unknown; ip?: string | null };

export type SolicitacaoPainel = {
  id: string;
  tipo: string;
  titulo: string;
  resumo: string;
  status: StatusSolicitacao;
  dados: { de?: Etapa; para?: Etapa; inicio?: string; subconta?: string | null; mesa?: string | null; plataforma?: Plataforma | null; etapa?: Etapa; [k: string]: unknown };
  mensagem: string | null;
  observacao: string | null;
  valor_centavos: number | null;
  /** Pago pela plataforma (evento) ou marcado por fora pelo operador (spec 007, M7 e M8). */
  pagamento: { pago_em: string; como: "PLATAFORMA" | "POR_FORA"; meio: string | null; pedido: string | null; observacao: string | null } | null;
  pessoa: Pessoinha;
  matricula: { id: string; status: StatusMatricula; plano: { slug: string; nome: string } };
  /** A subconta do plano em andamento (spec 059); sem ela, null. */
  subconta?: string | null;
  criada_em: string;
  decidida_em: string | null;
  decidida_por: string | null;
};

export type ItemDaFila = {
  solicitacao_id: string;
  matricula_id: string;
  pessoa: Pessoinha;
  plano: { slug: string; nome: string };
  inicio: string;
  inicio_formatado: string;
  atraso_dias: number;
  etapa_inicial: Etapa | null;
  etapa_inicial_rotulo: string | null;
  plataforma: Plataforma | null;
  plataforma_rotulo: string | null;
  mesas: { conta: string; nome: string }[];
  mesa_sugerida: string | null;
  pedido_em: string;
};

/** Avanco aprovado esperando a conta na extranet (spec 015, decisao 1): a etapa so muda quando alguem registra. */
export type AvancoALiberar = {
  solicitacao_id: string;
  matricula_id: string;
  trader: string;
  cpf: string;
  plano: string;
  de: Etapa;
  de_rotulo: string;
  para: Etapa;
  para_rotulo: string;
  plataforma: Plataforma | null;
  /** "criar" (conta nova na Mesa Real) ou "mover" (a mesma conta muda de mesa). */
  o_que_fazer: "criar" | "mover";
  aprovada_em: string;
  prazo: string | null;
  vence_em: string | null;
  atrasado: boolean;
};

/**
 * Pedido de aprovacao do Challenge aprovado (e pago, quando o plano cobra taxa de ativacao) esperando a conta Funded
 * na extranet (spec 064, modulo `nelogica-v2`). Prazo e atraso vem do backend.
 */
export type FundedALiberar = {
  solicitacao_id: string;
  matricula_id: string;
  cliente: string;
  cpf: string;
  plano: string;
  conta_do_challenge: string | null;
  taxa_de_ativacao_centavos: number | null;
  aprovada_em: string;
  prazo: string | null;
  vence_em: string | null;
  atrasado: boolean;
};
/** A taxa de ativacao de cada plano (spec 064, 2.4): sem linha, o plano nao cobra. */
export type TaxaDeAtivacao = { plano_slug: string; valor_centavos: number; atualizado_em: string };
/** A taxa de manutencao propria de um plano (spec 065, 2.1), em centavos de dolar; plano sem linha usa o padrao. */
export type TaxaDeManutencaoDoPlano = { plano_slug: string; valor_usd_centavos: number; atualizado_em: string };
export type TaxasDeManutencao = { padrao_usd_centavos: number; itens: TaxaDeManutencaoDoPlano[] };
type Limite = [number, number];
/** Configuração da Funded > cobranca da taxa de manutencao (spec 065, 2.2): o de agora, o do regulamento e os limites. */
export type ConfiguracaoDaManutencao = {
  valor_usd_centavos: number;
  nasce_dias_antes: number;
  ciclo_dias: number;
  regularizar_dias: number;
  atualizado_por: string | null;
  atualizado_em: string | null;
  padrao: { valor_usd_centavos: number; nasce_dias_antes: number; ciclo_dias: number; regularizar_dias: number };
  limites: { valor_usd_centavos: Limite; nasce_dias_antes: Limite; ciclo_dias: Limite; regularizar_dias: Limite };
};
/** Configuração da Funded > prazos do pedido de aprovacao (spec 065, 2.3). O rotulo ("2 dias úteis") sai do backend. */
export type PrazosDaAprovacao = {
  analise_horas: number;
  analise_rotulo: string;
  liberacao_horas: number;
  liberacao_rotulo: string;
  atualizado_por: string | null;
  atualizado_em: string | null;
  padrao: { analise_horas: number; liberacao_horas: number };
  limites: { analise_horas: Limite; liberacao_horas: Limite };
};
/** O pedido de aprovacao do Challenge guarda os prazos e, depois de liberado, a conta nova (spec 064). */
export type DadosDaAprovacao = { ciente_de_pausar_em?: string; analise_vence_em?: string; aguardando_liberacao?: boolean; prazo?: string; vence_em?: string; liberacao?: { dia: string; conta: string; conta_anterior: string | null } };

export type MatriculaPainel = {
  id: string;
  pessoa?: Pessoinha;
  plano: { slug: string; nome: string };
  status: StatusMatricula;
  tem_acesso: boolean;
  origem: string;
  pedido: string | null;
  comprada_em: string | null;
  ativada_em: string | null;
  suspensa_em: string | null;
  motivo_suspensao: { codigo: string; rotulo: string } | null;
  detalhe_suspensao: string | null;
  encerrada_em: string | null;
  motivo_encerramento: { codigo: string; rotulo: string } | null;
  detalhe_encerramento: string | null;
  criada_por: string | null;
  criado_em: string;
  /** A subconta do plano em andamento, nas listas (spec 059); sem ela, null. */
  subconta?: string | null;
};

/** Regra do proximo avanco, como o backend decide (spec 007, 4.4): a tela so mostra os campos que ela pede. */
export type ProximoAvanco = { para: Etapa; para_rotulo: string; portao: "SEMPRE" | "SE_PRO" | "NUNCA"; pede_plataforma: boolean; pede_subconta_nova: boolean; prazo: string };
export type ModuloEtapas = { familia: Familia; familia_rotulo: string; etapa: Etapa; etapa_rotulo: string; caminho: { etapa: Etapa; rotulo: string }[]; proxima_etapa: Etapa | null; proximo_avanco: ProximoAvanco | null; inicio: string; inicios: { etapa: Etapa; rotulo: string; numero: number; dia: string | null }[]; inicio_da_etapa: string | null; teste_ate: string | null; teste_sem_prazo: boolean; prazo_motivo: string | null; dias_restantes_do_teste: number | null; teste_vencido: boolean; teste_aprovado_em: string | null };
export type ModuloCorretora = { plataforma: Plataforma | null; plataforma_rotulo: string | null; subconta: string | null; mesa: string | null; mesa_nome: string | null; vinculada_em: string | null; declarada_por?: string | null };
/**
 * A conta da Global na corretora (modulo `nelogica-v2`, spec 043), como o painel recebe: bloqueada e desativada
 * separados, como a corretora manda. `estado` nulo: plano encerrado (a conta pode estar com outro plano agora).
 */
export type EtapaDaGlobal = "CHALLENGE" | "FUNDED";
export type ModuloContaNaCorretora = { conta: string; vinculada_em: string | null; estado: EstadoNaCorretora | null; /** Spec 044: sem registro, Challenge desde a ativacao (o backend ja manda assim). */ etapa?: EtapaDaGlobal | null; etapa_rotulo?: string | null; etapa_desde?: string | null; bloqueada?: boolean | null; desativada?: boolean | null; bloqueada_desde?: string | null; lida_em?: string | null; confirmada_em?: string | null; declarada_por?: string | null };
/** O saldo da conta (modulo `saldo`, spec 043): o resumo vem pronto do backend; o tipo mora no design system. */
export type ModuloSaldo = ResumoDoSaldo;
export type ModuloAgendamento = { inicio: string; inicio_formatado: string; escolhida_em: string; trocada_em: string };
export type Transicao = { de: StatusMatricula | null; para: StatusMatricula; motivo: { codigo: string; rotulo: string } | null; detalhe: string | null; ator: string; em: string };
export type DetalheDaMatricula = {
  matricula: MatriculaPainel;
  /** Acoes que o backend aceita agora pra esta matricula (nucleo e modulos). O painel so mostra essas. */
  acoes: string[];
  proximo_passo: { tipo: string; titulo: string; texto: string } | null;
  modulos: { etapas?: ModuloEtapas; corretora?: ModuloCorretora; agendamento?: ModuloAgendamento; mensalidade?: ModuloMensalidade; [k: string]: unknown };
  /** O que um modulo bloqueia agora (ex.: mensalidade vencida): o mesmo que a area do trader ve. */
  bloqueio: { motivo: string; mensagem: string } | null;
  /** CPF bloqueado pra compras novas (spec 003, decisao 19). */
  cpf_bloqueado: { motivo: string; bloqueado_em: string } | null;
  transicoes: Transicao[];
  auditoria: Auditoria[];
};
/** SUSPENSA so na taxa de manutencao da Global (spec 064): a parcela vencida suspendeu o plano. */
export type SituacaoMensalidade = "EM_DIA" | "VENCE_EM_BREVE" | "VENCE_HOJE" | "VENCIDA" | "VALOR_A_DEFINIR" | "SEM_COBRANCA" | "SUSPENSA";
/** Uma mensalidade (spec 007, secao 12). Situacao e textos vem do backend. */
export type Mensalidade = {
  id: string;
  numero: number;
  vencimento: string;
  vencimento_formatado: string;
  valor_centavos: number | null;
  status: "A_PAGAR" | "PAGA" | "CANCELADA";
  situacao: SituacaoMensalidade | "PAGA" | "CANCELADA";
  situacao_rotulo: string;
  dias_de_atraso: number;
  como_pagou: "PLATAFORMA" | "POR_FORA" | null;
  meio: string | null;
  paga_em: string | null;
  observacao: string | null;
  motivo_cancelamento: string | null;
  do_avanco: boolean;
  pode_pagar: boolean;
  /** Lancada a mao pela equipe (spec 041, 3.3): numeracao propria ("Avulsa 1"). */
  avulsa: boolean;
  /** Nome proprio da avulsa, quando ela tem um (spec 071: "Multa de reativação"). */
  rotulo?: string | null;
  /** O que o administrador pode fazer nela agora (spec 041). A tela so mostra estas; o backend confere de novo. */
  acoes: AcaoDaParcela[];
  /** Taxa de manutencao da Global (spec 064): o nome pronto, o valor em dolar e a cotacao, e o prazo da suspensao. */
  titulo?: string;
  valor_usd_centavos?: number | null;
  cotacao?: string | null;
  cotacao_publicada_em?: string | null;
  suspendeu_no_dia?: string | null;
  suspensa_ate?: string | null;
  prazo_encerrado?: boolean;
};
export type AcaoDaParcela = "marcar_paga" | "mudar_vencimento" | "cancelar" | "desfazer_paga";
/** "Mensalidade 3" ou "Avulsa 1": a avulsa tem numeracao propria. Quando o backend manda o nome pronto (`titulo` ou `rotulo`), vale ele. */
export const tituloDaParcela = (p: { numero: number; avulsa?: boolean; titulo?: string; rotulo?: string | null }) => p.titulo ?? p.rotulo ?? `${p.avulsa ? "Avulsa" : "Mensalidade"} ${p.numero}`;
/**
 * As contagens de Mensalidades e do cartao da visao geral (spec 041, 3.6), como o backend conta. Suspensas, a cancelar
 * e Funded sem taxa so vem da taxa de manutencao da Global (spec 064).
 */
export type ContagensDeMensalidade = { vencidas: number; vencidas_centavos: number; a_vencer_7_dias: number; pagas_no_mes: number; recebido_no_mes_centavos: number; suspensas?: number; a_cancelar?: number; funded_sem_taxa?: number };

// ---------------------------------------------------------------- dashboard (spec 053)
type PeriodoDasMetricas = { de: string; ate: string };
type ComAnterior<T> = T & { anterior: T };
/** O que o backend conta pra Visao Geral. `etapas` e `mensalidades` so vem onde o modulo dono esta ligado. */
export type MetricasDoPainel = {
  periodo: PeriodoDasMetricas;
  anterior: PeriodoDasMetricas;
  agrupamento: "dia" | "mes";
  chaves: string[];
  nucleo: {
    nunca_entraram: number;
    planos: { ativos: number; aguardando_inicio: number; suspensos: number; por_plano: { slug: string; nome: string; ativos: number }[] };
    pedidos_por_tipo: { tipo: string; titulo: string; quantidade: number; anterior: number }[] | null;
  };
  etapas?: {
    agora: { etapa: Etapa; rotulo: string; quantidade: number }[];
    entradas: { destinos: { chave: "liberadas" | "simulador" | "mesa_real"; rotulo: string; quantidade: number; anterior: number }[]; serie: { chave: string; liberadas: number; simulador: number; mesa_real: number }[] };
    avaliacao: AvaliacaoDaFamilia[];
    taxa_por_mes: { mes: string; aprovados: number; reprovados: number; taxa: number | null }[];
    repasses: ComAnterior<{ valor_centavos: number; quantidade: number }>;
  };
  mensalidades?: { vencidas: number; vencidas_centavos: number; a_vencer_7_dias: number; recebido: ComAnterior<{ valor_centavos: number; quantidade: number }> };
};
export type AvaliacaoDaFamilia = {
  familia: Familia;
  rotulo: string;
  etapa_de_avaliacao: string;
  em_avaliacao: number;
  aprovados: number;
  reprovados: number;
  finalizados: number;
  taxa: number | null;
  anterior: { aprovados: number; reprovados: number; taxa: number | null };
  por_plano: { slug: string; nome: string; em_avaliacao: number; aprovados: number; reprovados: number; taxa: number | null }[];
};
export type ModuloMensalidade = {
  paga_mensalidade: boolean;
  sem_cobranca_motivo: string | null;
  valor_centavos: number | null;
  /** O que o checkout da compra combinou (ou o ADMIN corrigiu): um valor por plataforma. So na Prop. */
  valores?: { profit_one: number | null; profit_pro: number | null; origem?: "PLANO" | "CHECKOUT" | "OPERADOR" | null };
  /** Taxa de manutencao da Global (spec 064): o nome, o valor em dolar e, suspenso, o ultimo dia pra pagar. */
  rotulo?: string;
  valor_usd_centavos?: number;
  suspensa_ate?: string | null;
  sem_taxa?: boolean;
  /** Spec 066: o plano isento da taxa de manutencao (so no painel), com o motivo e se ja esta na Funded. */
  isencao?: { motivo: string; criada_por: string; criada_em: string; na_funded: boolean } | null;
  /** Spec 065: o tamanho do ciclo aberto (ou o de agora, sem ciclo). So na taxa de manutencao. */
  ciclo_dias?: number;
  texto: string | null;
  situacao: SituacaoMensalidade;
  situacao_rotulo: string;
  proxima: Mensalidade | null;
  vencidas: number;
  /** Plano encerrado por inadimplencia (spec 071, 2.7 e 2.8): o que reabrir pede, e o valor padrao da multa. */
  reativacao?: "MULTA" | "SEM_REATIVACAO" | null;
  multa_padrao_centavos?: number;
  ultima_paga: { numero: number; vencimento: string; paga_em: string; como_pagou: string } | null;
};
export type LinhaDeMensalidade = Mensalidade & { etapa?: Etapa | null; matricula: { id: string; status: StatusMatricula; plano: { slug: string; nome: string }; subconta?: string | null }; pessoa: Pessoinha };
export type MensalidadesDaMatricula = { rotulo?: string; resumo: ModuloMensalidade | null; ciclos: { id: string; ancora: string; /** Spec 065: o tamanho que o ciclo guardou quando abriu (so na taxa de manutencao). */ ciclo_dias?: number; origem: string; criado_por?: string | null; criado_em: string; encerrado_em: string | null; motivo: string | null; mensalidades: Mensalidade[] }[] };
/** Uma linha de "Pagamentos em atraso" (spec 071). Rotulos, tag, dias de atraso e decisoes que cabem vem prontos do backend. */
export type DecisaoDoAtraso = "AGUARDAR" | "VALEU_PRA_OUTRO_PLANO" | "DEVOLVIDO" | "OUTRO";
export type PagamentoEmAtraso = {
  id: string;
  tipo: "PARCELA_VENCIDA" | "COMPROVANTE_DE_CANCELADA" | "PAGA_DEPOIS_DE_CANCELADA";
  tipo_rotulo: string;
  /** "A decidir", "Aguardando até dd/mm/aaaa" ou "Fechado". */
  tag: string;
  valor_centavos: number | null;
  criado_em: string;
  pessoa: { id: string; nome: string } | null;
  plano: { matricula_id: string; nome: string; status: StatusMatricula; encerrado_em: string | null; motivo_encerramento: string | null; detalhe_encerramento: string | null } | null;
  parcela: { id: string; numero: number; avulsa: boolean; rotulo: string | null; vencimento_formatado: string; status: Mensalidade["status"]; dias_de_atraso: number } | null;
  comprovante: { id: string; estado: EstadoDoComprovante; pago_no_dia_formatado: string; enviado_em: string; arquivo: string } | null;
  decisao: DecisaoDoAtraso | "RESOLVIDA" | null;
  decisao_rotulo: string | null;
  motivo: string | null;
  decidido_em: string | null;
  decisoes: { valor: DecisaoDoAtraso; rotulo: string }[];
};
export type Motivo = { codigo: string; rotulo: string; exige_detalhe: boolean };
export type Motivos = { encerramento: Motivo[]; suspensao: Motivo[] };
export type Mesa = { conta: string; nome: string; etapa: Etapa; ordem: number; ativa: boolean };
export type Plano = { slug: string; nome: string; nome_curto: string | null; descricao: string | null; destaques: string[]; contratos: number | null; ativo: boolean; ordem: number; matriculas: number };
export type FamiliaDoPlano = { plano: string; familia: Familia | null; etapa_inicial: Etapa | null };
export type Relogio = { agora: string; deslocamento_horas: number };
/** "Gerenciar plano" (spec 037): o estado atual e as opcoes validas, como o `GET .../gestao` manda. Sem `etapas` (Global), so o plano. */
export type GestaoDaMatricula = {
  status: StatusMatricula;
  encerrada: boolean;
  plano: { slug: string; nome: string };
  planos: { slug: string; nome: string }[];
  modulos: {
    etapas?: {
      familia: { codigo: Familia; rotulo: string } | null;
      /** Nulo: o plano ainda nao foi liberado (a etapa, a conta e a plataforma nascem na liberacao). */
      etapa: { codigo: Etapa; rotulo: string } | null;
      etapas_da_familia: { codigo: Etapa; rotulo: string }[];
      etapas_por_familia: Record<string, { codigo: Etapa; rotulo: string }[]>;
      inicios: { etapa: Etapa; rotulo: string; dia: string | null }[];
      conta: { mesa: string | null; subconta: string | null };
      mesas_por_etapa: Partial<Record<Etapa, { conta: string; nome: string }[]>>;
      plataforma: Plataforma | null;
      plataformas: { codigo: Plataforma; rotulo: string }[];
    };
    /** Global (spec 044, modulo nelogica-v2): etapa com o dia (AAAA-MM-DD) e a conta. Sem conta ainda, o caminho e o "Liberar conta". */
    corretora?: {
      etapa: { codigo: EtapaDaGlobal; rotulo: string; desde: string; registrada: boolean };
      etapas: { codigo: EtapaDaGlobal; rotulo: string }[];
      conta: string | null;
      pode_mudar_conta: boolean;
    };
  };
};
export type MudancaDaGestao = { campo: string; rotulo: string; antes: string | null; depois: string | null };
export type CompraSimulada = { pessoa_id: string; cpf: string; pix_e2e?: string; pedido?: string; matricula_ids: string[]; link_primeiro_acesso: string | null };
/** Um lote de clientes de exemplo (spec 055): cada situacao com o cliente que ficou nela, ou o erro. */
export type ClienteDeExemplo = { situacao: number; titulo: string; ok: boolean; erro?: string; pessoa_id?: string; nome?: string; cpf?: string; matricula_id?: string | null; link_primeiro_acesso?: string | null };
export type LoteDeExemplo = { lote: string; senha: string; itens: ClienteDeExemplo[]; vendas: { enviadas: number; ok: boolean; erro?: string } };

// ---------------------------------------------------------------- rotulos (so exibicao)

export const STATUS_DA_MATRICULA: Record<StatusMatricula, { rotulo: string; tom: Tom }> = {
  AGUARDANDO_INICIO: { rotulo: "Aguardando início", tom: "info" },
  ATIVA: { rotulo: "Ativa", tom: "sucesso" },
  SUSPENSA: { rotulo: "Suspensa", tom: "alerta" },
  ENCERRADA: { rotulo: "Encerrada", tom: "neutro" },
};
export const STATUS_DA_SOLICITACAO: Record<StatusSolicitacao, { rotulo: string; tom: Tom }> = {
  PENDENTE: { rotulo: "Pendente", tom: "info" },
  AGUARDANDO_PAGAMENTO: { rotulo: "Aguardando pagamento", tom: "alerta" },
  APROVADA: { rotulo: "Aprovada", tom: "sucesso" },
  RECUSADA: { rotulo: "Recusada", tom: "erro" },
  CANCELADA: { rotulo: "Cancelada", tom: "neutro" },
  REVOGADA: { rotulo: "Revogada", tom: "erro" },
};
export const ETAPA: Record<Etapa, string> = { TESTE: "Período de teste", SIMULADOR: "Simulador Remunerado", MESA_REAL: "Mesa Real" };
export const FAMILIA: Record<Familia, string> = { EXAME: "Exame", FAST: "Fast", BLACK: "Black" };
export const PLATAFORMA: Record<Plataforma, string> = { PROFIT_ONE: "Profit One", PROFIT_PRO: "Profit Pro" };
/** De onde veio o pedido de mudanca (spec 028): os valores sao os do backend (`ORIGENS_DA_MUDANCA`). */
export const ORIGENS_DA_MUDANCA: { valor: string; rotulo: string }[] = [
  { valor: "CONFERENCIA_INTERNA", rotulo: "Conferência interna (ninguém pediu)" },
  { valor: "CLIENTE_WHATSAPP", rotulo: "Cliente pelo WhatsApp" },
  { valor: "CLIENTE_EMAIL", rotulo: "Cliente por e-mail" },
  { valor: "CLIENTE_AREA", rotulo: "Cliente pela área" },
  { valor: "CORRETORA", rotulo: "Corretora" },
  { valor: "OUTRO", rotulo: "Outro" },
];
export const TIPOS_DE_SOLICITACAO: Record<string, string> = { LIBERACAO: "Liberação da plataforma", AVANCO_ETAPA: "Avanço de etapa", PEDIDO_DE_REPASSE: "Pedido de repasse", APROVACAO_DO_CHALLENGE: "Aprovação do Challenge" };
/** O modulo dono de cada tipo (o mesmo do catalogo do backend): o filtro so oferece o que existe no ambiente. */
export const MODULO_DO_TIPO: Record<string, string> = { LIBERACAO: "agendamento", AVANCO_ETAPA: "etapas", PEDIDO_DE_REPASSE: "etapas", APROVACAO_DO_CHALLENGE: "nelogica-v2" };

// ---------------------------------------------------------------- papel

const ORDEM: Record<Operador["role"], number> = { OWNER: 4, ADMIN: 3, SUPORTE: 2, ANALISTA: 1 };
/** O backend e quem barra (403); isto so esconde ou explica o botao que o papel nao pode usar. */
export function usePapel() {
  const { estado } = useSessao();
  const role = estado.status === "logado" ? estado.usuario.role : "ANALISTA";
  return { role, pode: (minimo: Operador["role"]) => ORDEM[role] >= ORDEM[minimo] };
}

// ---------------------------------------------------------------- chamadas

/** POST, PUT e PATCH sempre com corpo JSON ("{}" quando nao ha): o backend recusa JSON vazio. */
export function useApi() {
  const { api, arquivo } = useSessao();
  return {
    ler: <T,>(caminho: string) => api<T>(caminho),
    postar: <T,>(caminho: string, corpo: unknown = {}) => api<T>(caminho, { method: "POST", body: JSON.stringify(corpo) }),
    por: <T,>(caminho: string, corpo: unknown) => api<T>(caminho, { method: "PUT", body: JSON.stringify(corpo) }),
    mudar: <T,>(caminho: string, corpo: unknown) => api<T>(caminho, { method: "PATCH", body: JSON.stringify(corpo) }),
    // Com corpo: apagar repasse passou a exigir motivo (spec 031, 3.10). Sem ele vai "{}": a chamada sempre leva
    // Content-Type JSON e o Fastify recusa corpo vazio nesse tipo (400 FST_ERR_CTP_EMPTY_JSON_BODY), como no POST.
    apagar: <T,>(caminho: string, corpo: unknown = {}) => api<T>(caminho, { method: "DELETE", body: JSON.stringify(corpo) }),
    arquivo,
  };
}

/** Mensagem do backend (ja escrita pro operador), com as recusas de papel e de rota explicadas. */
export function erroDe(err: unknown): string {
  if (err instanceof ErroApi && err.status === 403) return "Seu papel não permite esta ação. Peça a um admin.";
  if (err instanceof ErroApi && err.status === 404 && !err.corpo?.erro) return "Esta função ainda não existe neste backend. Atualize o ambiente.";
  return mensagemDe(err, "Não deu certo agora. Tente de novo em instantes.");
}

/** Uma acao da tela: carregando, erro em destaque e `rodar`, que devolve se deu certo. */
export function useAcao() {
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState<string>();
  const rodar = useCallback(async (f: () => Promise<unknown>): Promise<boolean> => {
    setCarregando(true);
    setErro(undefined);
    try {
      await f();
      return true;
    } catch (err) {
      setErro(erroDe(err));
      return false;
    } finally {
      setCarregando(false);
    }
  }, []);
  return { carregando, erro, rodar, limpar: useCallback(() => setErro(undefined), []) };
}

/** "operador:abc" vira "Administrador"; "pessoa:x" vira "Cliente"; "sistema" fica (ADR-011). So pra ler a trilha. */
export function quem(ator: string | null | undefined): string {
  if (!ator) return "sistema";
  if (ator.startsWith("operador:")) return "Administrador";
  if (ator.startsWith("pessoa:")) return "Cliente";
  return ator;
}

// ---------------------------------------------------------------- trilha

const ACAO: Record<string, string> = {
  SOLICITACAO_APROVADA: "Pedido aprovado",
  SOLICITACAO_AGUARDANDO_PAGAMENTO: "Aprovado, aguardando pagamento",
  SOLICITACAO_RECUSADA: "Pedido recusado",
  SOLICITACAO_REVOGADA: "Avanço revogado",
  SOLICITACOES_CANCELADAS: "Pedidos em aberto cancelados",
  COMPROVANTE_ENVIADO: "Comprovante enviado",
  COMPROVANTE_CONFERIDO: "Comprovante conferido",
  COMPROVANTE_RECUSADO: "Comprovante recusado",
  COMPROVANTE_BAIXADO: "Comprovante aberto",
  INICIO_AGENDADO: "Início agendado",
  INICIO_REAGENDADO: "Início reagendado",
  MATRICULA_CRIADA: "Matrícula criada",
  MATRICULA_GERENCIADA: "Plano corrigido em Gerenciar plano",
  PLANO_ANTIGO_ADICIONADO: "Plano antigo adicionado",
  ACADEMY_CURSO_EXCLUIDO: "Curso da Academy excluído",
  ACADEMY_MODULO_EXCLUIDO: "Módulo da Academy excluído",
  ACADEMY_AULA_EXCLUIDO: "Aula da Academy excluída",
  PLANO_ANTIGO_CORRIGIDO: "Plano antigo corrigido",
  PLANO_ANTIGO_APAGADO: "Plano antigo excluído",
  BENEFICIO_PROMETIDO: "Benefício prometido",
  BENEFICIO_HONRADO: "Benefício honrado",
  BENEFICIO_CANCELADO: "Benefício cancelado",
  PLANO_EM_DOBRO_CRIADO: "Plano bônus criado",
  REPASSE_REGISTRADO: "Repasse registrado",
  REPASSE_CORRIGIDO: "Repasse corrigido",
  REPASSE_APAGADO: "Repasse apagado",
  REPASSE_PEDIDO: "Repasse pedido pelo cliente",
  REPASSE_PEDIDO_PELO_WHATSAPP: "Repasse combinado no WhatsApp",
  REPASSE_CALCULO_ENVIADO: "Cálculo do repasse enviado",
  REPASSE_CALCULO_REFEITO: "Cálculo do repasse refeito",
  REPASSE_CALCULO_CONFIRMADO: "Cliente confirmou o cálculo",
  REPASSE_CALCULO_RECUSADO: "Cliente não concordou com o cálculo",
  REPASSE_PEDIDO_CONCLUIDO: "Pagamento feito",
  LEMBRETE_CRIADO: "Lembrete pós-repasse criado",
  LEMBRETE_ITEM_MARCADO: "Item do lembrete marcado",
  LEMBRETE_ITEM_DESMARCADO: "Item do lembrete desmarcado",
  LEMBRETE_CONCLUIDO: "Lembrete pós-repasse concluído",
  AVANCO_PEDIDO_PELA_EQUIPE: "Análise pedida pela equipe",
  MATRICULA_ATIVADA: "Matrícula ativada",
  MATRICULA_SUSPENSA: "Matrícula suspensa",
  MATRICULA_RETOMADA: "Matrícula retomada",
  MATRICULA_ENCERRADA: "Matrícula encerrada",
  MATRICULA_REABERTA: "Encerramento desfeito",
  MATRICULA_PLANO_TROCADO: "Plano trocado",
  ETAPA_AVANCADA: "Etapa avançada",
  AVANCO_REVOGADO: "Avanço revogado",
  PLATAFORMA_DEFINIDA: "Plataforma definida",
  PRAZO_DO_TESTE_ALTERADO: "Prazo do teste alterado",
  TESTE_VENCIDO: "Prazo do teste vencido",
  FAMILIA_DO_PLANO_DEFINIDA: "Família do plano definida",
  COMPRA_SIMULADA: "Compra simulada",
  MENSALIDADE_VENCIMENTO_MUDADO: "Vencimento da mensalidade mudado",
  MENSALIDADE_PAGA_DESFEITA: "Pagamento da mensalidade desfeito",
  MENSALIDADE_LANCADA: "Mensalidade avulsa lançada",
  MENSALIDADE_CANCELADA_A_MAO: "Mensalidade cancelada",
  PAGAMENTO_EM_ATRASO_DECIDIDO: "Pagamento em atraso decidido",
  MULTA_DE_REATIVACAO_COBRADA: "Multa de reativação cobrada",
  MULTA_DE_REATIVACAO_DISPENSADA: "Multa de reativação dispensada",
  EMAIL_ADICIONADO: "E-mail acrescentado",
  EMAIL_REMOVIDO: "E-mail removido",
  EMAIL_PRINCIPAL_ALTERADO: "E-mail de acesso trocado",
  EMAIL_VERIFICADO: "E-mail verificado",
  EMAIL_ANEXADO_PELA_COMPRA: "E-mail anexado pela compra",
  EMAIL_RECUPERADO: "E-mail trocado na recuperação de acesso",
  LINK_DE_ACESSO_REENVIADO: "Link de acesso reenviado",
  PESSOA_ATUALIZADA: "Dados do cadastro mudados",
  SENHA_DEFINIDA: "Senha definida",
  REGULAMENTO_REGISTRADO_PELO_ADMINISTRADOR: "Regulamento registrado pelo administrador",
  // Spec 064 (Global): o pedido de aprovacao do Challenge, a Funded e as taxas.
  APROVACAO_DO_CHALLENGE_PEDIDA: "Aprovação do Challenge pedida",
  FUNDED_LIBERADA: "Conta Funded liberada",
  TAXA_DE_ATIVACAO_DO_PLANO_DEFINIDA: "Taxa de ativação do plano definida",
  TAXA_DE_MANUTENCAO_INICIADA: "Taxa de manutenção iniciada",
  TAXA_DE_MANUTENCAO_PAGA: "Taxa de manutenção paga",
  TAXA_DE_MANUTENCAO_VENCIMENTO_MUDADO: "Vencimento da taxa de manutenção mudado",
  TAXA_DE_MANUTENCAO_CANCELADA_A_MAO: "Taxa de manutenção cancelada",
};
export const nomeDaAcao = (acao: string) => ACAO[acao] ?? acao.charAt(0) + acao.slice(1).toLowerCase().replaceAll("_", " ");

/** Chaves de auditoria que so repetem o que a tela ja mostra (ids, tipo). */
const OCULTAS = new Set(["tipo", "matriculaId", "pessoaId", "solicitacaoId", "comprovanteId", "id", "aguardando_liberacao"]);
const RENOMEADAS: Record<string, string> = { valorCentavos: "valor", valor_centavos: "valor", contarDe: "contar de", semPrazo: "sem prazo", testeAte: "teste até", grupo: "versão", ciente_de_pausar_em: "pausa confirmada", analise_vence_em: "decidir até", vence_em: "liberar até", aprovada_em: "aprovado em", conta_anterior: "conta anterior" };

/**
 * `depois` da auditoria em uma linha legivel ("subconta: 296, mesa: 103166316"). Abre um nivel de objeto (`dados`),
 * esconde ids e escreve dinheiro em reais. So leitura da trilha; o dado cru segue no banco.
 */
export function resumoDe(x: unknown): string {
  if (!x || typeof x !== "object") return x == null ? "" : String(x);
  const pares: string[] = [];
  const juntar = (o: Record<string, unknown>) => {
    for (const [k, v] of Object.entries(o)) {
      if (v === null || v === undefined || v === "" || OCULTAS.has(k)) continue;
      if (typeof v === "object" && !Array.isArray(v)) { juntar(v as Record<string, unknown>); continue; }
      // Instante cru ("2026-10-20T12:00:00.000Z") e UTC: na trilha vira a hora de Brasilia (19/10).
      const valor = (k === "valorCentavos" || k === "valor_centavos") && typeof v === "number" ? formatarReais(v) : Array.isArray(v) ? v.join(", ") : typeof v === "string" && ehInstante(v) ? formatarDataHora(v) : String(v);
      pares.push(`${RENOMEADAS[k] ?? k}: ${valor}`);
    }
  };
  juntar(x as Record<string, unknown>);
  return pares.join(", ");
}
